import {
  WSAlert,
  WSButton,
  WSCard,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSLoader,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { IAddress } from "@wingspanhq/users/dist/lib/interfaces";
import React from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  routeHelpersEmployeeTaxForms,
  routeHelpersEmployeeTaxFormsForm,
  routeHelpersEmployeeTaxFormsFormResult
} from "../paths";
import { useQueryEmployeeTaxFormSchema } from "../../../query/employeeTaxForms/queries/useQueryEmployeeTaxFormSchema";
import { selectorAddressString } from "../../../shared/selectors/selectorAddressString";
import {
  isSensitiveField,
  isSharedPiiField
} from "../../DynamicForm/utils/isSharedPiiField";
import { IFormSchemaField } from "../types";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const renderValue = (
  field: IFormSchemaField,
  value: unknown
): React.ReactNode => {
  if (value === undefined || value === null || value === "") {
    return (
      <WSText.ParagraphSm color="gray400">Not provided</WSText.ParagraphSm>
    );
  }
  if (field.type === "Boolean") {
    return <WSText.ParagraphSm>{value ? "Yes" : "No"}</WSText.ParagraphSm>;
  }
  if (field.type === "Address" && typeof value === "object") {
    const composed = selectorAddressString(
      value as Partial<IAddress>,
      false,
      ", "
    );
    return (
      <WSText.ParagraphSm>{composed || "Not provided"}</WSText.ParagraphSm>
    );
  }
  if (field.type === "Enum" && Array.isArray(field.options)) {
    const match = field.options.find(o => o.value === value);
    return (
      <WSText.ParagraphSm>{match?.label ?? String(value)}</WSText.ParagraphSm>
    );
  }
  if (isSensitiveField(field)) {
    return <WSText.ParagraphSm>•••••</WSText.ParagraphSm>;
  }
  return <WSText.ParagraphSm>{String(value)}</WSText.ParagraphSm>;
};

export const EmployeeTaxFormReview: React.FC<FlowStep> = () => {
  const {
    params: { formInstanceId }
  } = routeHelpersEmployeeTaxFormsFormResult.useData();
  const goToForm = routeHelpersEmployeeTaxFormsForm.useGoTo();
  const goToChecklist = routeHelpersEmployeeTaxForms.useGoTo();
  const querySchema = useQueryEmployeeTaxFormSchema(formInstanceId);

  if (querySchema.isLoading) {
    return (
      <WSFlexBox.Center py="3XL">
        <WSLoader.Spinner size="M" />
      </WSFlexBox.Center>
    );
  }

  if (querySchema.error || !querySchema.data) {
    return (
      <ErrorAlert
        context="EmployeeTaxFormSchema"
        error={querySchema.error ?? "Couldn't load filing"}
      />
    );
  }

  const response = querySchema.data;
  const { currentFiling } = response;

  // A corrected row serves a currentFiling WITH prior data but WITHOUT
  // completedAt — that filing is awaiting re-submission, not submitted, so
  // only completedAt marks a genuinely submitted filing.
  if (!currentFiling?.completedAt) {
    return (
      <WSList gap="XL">
        <WSEmptyState
          type="documents"
          title="Not submitted yet"
          description="This form has not been submitted yet."
          bgColor="gray"
        />
        <WSButton
          kind="Primary"
          fullWidth
          onClick={() => goToForm({ formInstanceId })}
        >
          Open form
        </WSButton>
      </WSList>
    );
  }

  const submittedAtText = currentFiling.completedAt
    ? toWSDateString(currentFiling.completedAt)
    : null;

  return (
    <WSList gap="XL">
      <WSAlert theme="success">{response.title} was submitted.</WSAlert>
      <WSText.Heading2>{response.title}</WSText.Heading2>
      {submittedAtText ? (
        <WSText.ParagraphSm color="gray500">
          Submitted on {submittedAtText}
        </WSText.ParagraphSm>
      ) : null}

      {response.sections.map(section => {
        const reviewFields = section.fields.filter(
          field =>
            field.type !== "SignatureBlock" &&
            field.type !== "ReadOnly" &&
            !isSharedPiiField(field)
        );
        if (reviewFields.length === 0) return null;
        return (
          <WSCard key={section.key}>
            <WSList gap="M">
              {section.title ? (
                <WSText.Heading5>{section.title}</WSText.Heading5>
              ) : null}
              {reviewFields.map(field => (
                <WSList key={field.key} gap="XS">
                  <WSText.ParagraphSm color="gray500">
                    {field.label}
                  </WSText.ParagraphSm>
                  {renderValue(field, currentFiling.data[field.key])}
                </WSList>
              ))}
            </WSList>
          </WSCard>
        );
      })}

      <WSButton kind="Secondary" fullWidth onClick={() => goToChecklist()}>
        Back to forms
      </WSButton>
    </WSList>
  );
};
